import { useState } from 'react';
import { toast } from '@theonefamily/ui';
import { useApi } from '../hooks/useApi.js';
import { post } from '../lib/api.js';

interface DarkWebMonitor {
  id: string;
  monitor_type: string;
  value: string;
  label?: string;
  is_active: boolean;
  last_scanned_at?: string;
  findings_count: number;
  unresolved_count: number;
}

interface DarkWebFinding {
  id: string;
  monitor_id: string;
  severity: string;
  status: string;
  source: string;
  breach_name?: string;
  breach_date?: string;
  exposed_data_types: string[];
  exposed_email?: string;
  password_exposed: boolean;
  created_at: string;
}

interface ListResponse {
  data: { monitors: DarkWebMonitor[]; findings: DarkWebFinding[] };
}

interface SummaryResponse {
  data: {
    total_monitors: number;
    active_monitors: number;
    total_findings: number;
    unresolved_findings: number;
    critical_findings: number;
    passwords_exposed: number;
  };
}

export function DarkWebPage() {
  const { data: listData, loading, error, refresh } = useApi<ListResponse>('/protect/dark-web?action=list');
  const { data: summaryData } = useApi<SummaryResponse>('/protect/dark-web?action=get_summary');
  const [showAddForm, setShowAddForm] = useState(false);
  const [formType, setFormType] = useState<'domain' | 'email' | 'keyword'>('domain');
  const [formValue, setFormValue] = useState('');
  const [formLabel, setFormLabel] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const monitors = listData?.data?.monitors ?? [];
  const findings = listData?.data?.findings ?? [];
  const summary = summaryData?.data;

  async function handleAddMonitor(e: React.FormEvent) {
    e.preventDefault();
    if (!formValue.trim()) return;
    setSubmitting(true);
    try {
      await post('/protect/dark-web', {
        monitor_type: formType,
        value: formValue.trim(),
        label: formLabel.trim() || undefined,
      });
      setShowAddForm(false);
      setFormValue('');
      setFormLabel('');
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add monitor');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResolve(findingId: string) {
    setActionLoading(findingId);
    try {
      await post(`/protect/dark-web/${findingId}?action=resolve_finding`, { status: 'resolved' });
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to resolve finding');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleRemoveMonitor(monitorId: string) {
    if (!confirm('Remove this monitor?')) return;
    setActionLoading(monitorId);
    try {
      await post(`/protect/dark-web/${monitorId}?action=remove_monitor`);
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to remove monitor');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleTriggerScan(monitorId?: string) {
    const id = monitorId ?? 'all';
    setActionLoading(`scan-${id}`);
    try {
      const path = monitorId
        ? `/protect/dark-web/${monitorId}?action=trigger_scan`
        : '/protect/dark-web?action=trigger_scan';
      await post(path);
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to trigger scan');
    } finally {
      setActionLoading(null);
    }
  }

  function severityBadge(severity: string) {
    const colors =
      severity === 'critical' ? 'bg-red-100 text-red-700' :
      severity === 'high' ? 'bg-orange-100 text-orange-700' :
      severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
      'bg-blue-100 text-blue-700';
    return <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors}`}>{severity}</span>;
  }

  function statusBadge(status: string) {
    const colors =
      status === 'new' ? 'bg-red-100 text-red-700' :
      status === 'acknowledged' ? 'bg-yellow-100 text-yellow-700' :
      status === 'resolved' ? 'bg-emerald-100 text-emerald-700' :
      'bg-gray-100 text-gray-600';
    return <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors}`}>{status}</span>;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading dark web monitoring data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">{error}</p>
        <button onClick={refresh} className="mt-2 text-sm text-red-600 underline">Retry</button>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Dark Web Monitoring</h2>
      <p className="text-gray-600 mb-6">Monitor your organization's credentials and data across dark web sources and breach databases.</p>

      {summary && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Active Monitors</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{summary.active_monitors}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Unresolved Findings</p>
            <p className="text-2xl font-bold text-red-600 mt-1">{summary.unresolved_findings}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Critical Findings</p>
            <p className="text-2xl font-bold text-red-600 mt-1">{summary.critical_findings}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Passwords Exposed</p>
            <p className="text-2xl font-bold text-orange-600 mt-1">{summary.passwords_exposed}</p>
          </div>
        </div>
      )}

      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Active Monitors</h3>
          <div className="flex gap-2">
            <button
              onClick={() => handleTriggerScan()}
              disabled={actionLoading === 'scan-all'}
              className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200 disabled:opacity-50"
            >
              {actionLoading === 'scan-all' ? 'Scanning...' : 'Scan All'}
            </button>
            <button
              onClick={() => setShowAddForm(true)}
              className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700"
            >
              + Add Monitor
            </button>
          </div>
        </div>

        {showAddForm && (
          <form onSubmit={handleAddMonitor} className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={formType}
                  onChange={(e) => setFormType(e.target.value as 'domain' | 'email' | 'keyword')}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="domain">Domain</option>
                  <option value="email">Email</option>
                  <option value="keyword">Keyword</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
                <input
                  type="text"
                  value={formValue}
                  onChange={(e) => setFormValue(e.target.value)}
                  placeholder={formType === 'domain' ? 'acmecorp.com' : formType === 'email' ? 'user@acmecorp.com' : 'keyword'}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Label (optional)</label>
                <input
                  type="text"
                  value={formLabel}
                  onChange={(e) => setFormLabel(e.target.value)}
                  placeholder="My domain"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div className="flex items-end gap-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700 disabled:opacity-50"
                >
                  {submitting ? 'Adding...' : 'Add'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        )}

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Monitor</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Type</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Findings</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Last Scan</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {monitors.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">No monitors configured. Add one to start monitoring.</td></tr>
              ) : monitors.map((m) => (
                <tr key={m.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{m.label || m.value}</td>
                  <td className="px-4 py-3 text-gray-600 capitalize">{m.monitor_type}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${m.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                      {m.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {m.unresolved_count > 0 ? (
                      <span className="text-red-600 font-medium">{m.unresolved_count} unresolved</span>
                    ) : (
                      <span>{m.findings_count}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-500">{m.last_scanned_at ? new Date(m.last_scanned_at).toLocaleDateString() : 'Never'}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleTriggerScan(m.id)}
                      disabled={actionLoading === `scan-${m.id}`}
                      className="text-xs text-emerald-600 hover:text-emerald-800 mr-3 disabled:opacity-50"
                    >
                      Scan
                    </button>
                    <button
                      onClick={() => handleRemoveMonitor(m.id)}
                      disabled={actionLoading === m.id}
                      className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Recent Findings</h3>
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Source</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Detail</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Severity</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Date</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {findings.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">No findings detected yet.</td></tr>
              ) : findings.map((f) => (
                <tr key={f.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-600">{f.source === 'hibp' ? 'Breach Database' : f.source === 'hibp_paste' ? 'Paste Site' : f.source}</td>
                  <td className="px-4 py-3 font-medium text-gray-900">
                    {f.exposed_email && `${f.exposed_email} — `}
                    {f.breach_name ?? 'Unknown breach'}
                    {f.password_exposed && <span className="ml-2 text-xs text-red-600 font-bold">PASSWORD</span>}
                  </td>
                  <td className="px-4 py-3">{severityBadge(f.severity)}</td>
                  <td className="px-4 py-3">{statusBadge(f.status)}</td>
                  <td className="px-4 py-3 text-gray-500">{new Date(f.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-right">
                    {(f.status === 'new' || f.status === 'acknowledged') && (
                      <button
                        onClick={() => handleResolve(f.id)}
                        disabled={actionLoading === f.id}
                        className="text-xs text-emerald-600 hover:text-emerald-800 disabled:opacity-50"
                      >
                        {actionLoading === f.id ? 'Resolving...' : 'Resolve'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
